import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { type RecallTopicCounts, recallsStateDocState, recallTopicCountsState, recallTopicsState } from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue } from '@/state/loadableHooks';
import {
    RECALL_TOPIC_FOLLOW_MAX_YEARS,
    RECALL_TOPIC_FOLLOW_STEP_MONTHS,
    RECALL_TOPIC_LOOKBACK_MONTHS,
} from '@rock/recalls';
import type { RecallsState, RecallTopic, RecallTopicId } from '@rock/types';
import { Pencil, Plus } from 'lucide-react';
import { type FC, useState } from 'react';
import { RecallTopicDialog } from './RecallTopicDialog';
import { RecallTopicIconGlyph } from './RecallTopicIconGlyph';

/**
 * The FYI topics, each with its awaiting and live counts and how many live recalls it had in the app's follow
 * windows; add or edit one here, and a row opens the topic.
 */
export const RecallTopicsTab: FC = () => {
    const topics = useAsyncAtomValue(recallTopicsState);
    const { value: counts } = useFreshOnMountAtomValue(recallTopicCountsState);
    const state = useAsyncAtomValue(recallsStateDocState);
    return (
        <TopicListCard
            topics={topics ?? []}
            loading={!topics}
            counts={counts}
            topicStats={state ? (state.topicStats ?? {}) : undefined}
        />
    );
};

const TopicListCard: FC<{
    topics: RecallTopic[];
    loading: boolean;
    counts: Partial<Record<RecallTopicId, RecallTopicCounts>> | undefined;
    topicStats: RecallsState['topicStats'];
}> = ({ topics, loading, counts, topicStats }) => {
    const onRowClick = useRowClick();
    const [editing, setEditing] = useState<{ topic?: RecallTopic }>();
    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                    <CardTitle>By topic</CardTitle>
                    <CardDescription>
                        Broad kinds of recall a home can follow, each from a date of its choosing. An approved recall
                        with a topic reaches every home following it from before it was published, as an FYI with no
                        notification; a follow reaches back up to {RECALL_TOPIC_FOLLOW_MAX_YEARS} years. The classifier
                        proposes topics; approve them here.
                    </CardDescription>
                </div>
                <Button variant="outline" onClick={() => setEditing({})}>
                    <Plus className="h-4 w-4" />
                    New Topic
                </Button>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Topic</TableHead>
                            <TableHead>Covers</TableHead>
                            <TableHead className="text-right">Awaiting</TableHead>
                            <TableHead className="text-right">Live</TableHead>
                            <TableHead className="text-right" title={liveWindowHint(RECALL_TOPIC_FOLLOW_STEP_MONTHS)}>
                                {RECALL_TOPIC_FOLLOW_STEP_MONTHS} mo
                            </TableHead>
                            <TableHead className="text-right" title={liveWindowHint(RECALL_TOPIC_LOOKBACK_MONTHS)}>
                                {RECALL_TOPIC_LOOKBACK_MONTHS} mo
                            </TableHead>
                            <TableHead className="w-10" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {topics.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                                    {loading ? 'Loading…' : 'No topics yet.'}
                                </TableCell>
                            </TableRow>
                        )}
                        {topics.map((topic) => (
                            <TableRow
                                key={topic.id}
                                className="cursor-pointer"
                                onClick={onRowClick(`/products/recalls/topics/${encodeURIComponent(topic.id)}`)}
                            >
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        <RecallTopicIconGlyph
                                            icon={topic.icon}
                                            className="h-5 w-5 shrink-0 text-muted-foreground"
                                        />
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-medium">{topic.title}</span>
                                            <span className="font-mono text-xs text-muted-foreground">{topic.id}</span>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="max-w-md whitespace-normal text-sm text-muted-foreground">
                                    {topic.description}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    <CountCell count={counts?.[topic.id]?.awaiting} loading={!counts} emphasis />
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    <CountCell count={counts?.[topic.id]?.approved} loading={!counts} />
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    <CountCell
                                        count={topicStats?.[topic.id]?.recentCount ?? (topicStats && 0)}
                                        loading={!topicStats}
                                    />
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    <CountCell
                                        count={topicStats?.[topic.id]?.lookbackCount ?? (topicStats && 0)}
                                        loading={!topicStats}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        aria-label={`Edit ${topic.title}`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setEditing({ topic });
                                        }}
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
            <RecallTopicDialog
                open={!!editing}
                onOpenChange={(open) => !open && setEditing(undefined)}
                topic={editing?.topic}
                topics={topics}
            />
        </Card>
    );
};

function liveWindowHint(months: number): string {
    return `Live recalls published in the past ${months} months; recounted by the daily sync and after a job`;
}

const CountCell: FC<{ count: number | undefined; loading: boolean; emphasis?: boolean }> = ({
    count,
    loading,
    emphasis,
}) => {
    if (count === undefined) return <span className="text-muted-foreground">{loading ? '…' : '—'}</span>;
    return <span className={cn(emphasis && count > 0 && 'font-semibold text-violet-700')}>{count}</span>;
};
